import { ItemMetadata } from '../common/api.types';

/**
 * User details from corporate system (PAPI)
 */
export interface UserDetailsFromCorpSystem {
  badge_color: string;
  business_title: string;
  cost_center_name: string;
  employee_status: string;
  first_name: string;
  job_status: string;
  last_name: string;
  legacy_employee_id: string;
  login: string;
  person_id: string;
}

/**
 * State interface for multiple user permissions (for admin page)
 */
export interface AllUserPermissionsState {
  data: UserPermission[];
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  hasSuccessfulFetch: boolean;
  lastFetchTimestamp: number | null;
}

/**
 * State interface for current user permissions (single user)
 */
export interface CurrentUserPermissionsState {
  data: UserPermission | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  hasSuccessfulFetch: boolean;
  lastFetchTimestamp: number | null;
}

/**
 * Combined Access Management slice state
 * Manages all permission-related data in one place
 *
 * Note: Confidential data map is now built from hierarchy data via selector
 * (no separate state needed)
 */
export interface AccessManagementState {
  allUserPermissions: AllUserPermissionsState;
  currentUserPermissions: CurrentUserPermissionsState;
}

export enum UserType {
  TECH_ADMIN = 'TECH_ADMIN',
  READ_ONLY = 'READ_ONLY',
  VERITY_ADMIN = 'VERITY_ADMIN',
  EXTENDED_ACCESS_PL = 'EXTENDED_ACCESS_PL',
}

/**
 * Product line access configuration
 *
 * Defines granular access control for users within a specific product line.
 * This interface controls both administrative privileges and confidential data access.
 *
 * @property {number} product_line_id - Unique identifier for the product line
 * @property {boolean} is_pl_admin - Indicates if user has administrative privileges for this product line
 * @property {boolean} has_confidential_access - Indicates if user can access confidential RPNs
 *
 * @remarks
 * **Access Level Combinations:**
 *
 * 1. **Product Line Administrator** (`is_pl_admin: true`, `has_confidential_access: true`)
 *    - Full read and write access to all RPNs, including confidential ones
 *    - Can manage product line
 *    - Highest level of access for a product line
 *
 * 2. **Confidential Read-Only** (`is_pl_admin: false`, `has_confidential_access: true`)
 *    - Read-only access to confidential data within the product line
 *    - Cannot edit or modify any data
 *    - Useful for stakeholders requiring visibility
 *
 * 3. **Standard Access** (`is_pl_admin: false`, `has_confidential_access: false`)
 *    - Standard user access without confidential data visibility
 *    - Access based on other permission rules
 *
 * @note
 * The combination `is_pl_admin: true` with `has_confidential_access: false` is not supported.
 * Product line administrators automatically receive confidential access as part of their
 * administrative role. All PL admins have full access to confidential data by default.
 *
 * @example
 * // Product Line Administrator
 * const plAdmin: ProductLineAccess = {
 *   product_line_id: 123,
 *   is_pl_admin: true,
 *   has_confidential_access: true
 * };
 *
 * @example
 * // Confidential viewer (read-only)
 * const confidentialViewer: ProductLineAccess = {
 *   product_line_id: 123,
 *   is_pl_admin: false,
 *   has_confidential_access: true
 * };
 */
export interface ProductLineAccess {
  product_line_id: number;
  is_pl_admin: boolean;
  has_confidential_access: boolean;
}

/**
 * Enhanced product line access with full product line information
 * Used in UI components that need complete product line details
 */
export interface EnhancedProductLineAccess extends ProductLineAccess {
  product_line: string;
  product_code: string;
  is_active: boolean;
}

/**
 * User permission record with LDAP-based access control
 */
export interface UserPermission {
  user_access_id: number | null; // ID - null for new records, number for existing records
  user_alias: string;
  is_dev: boolean; // LDAP group "verity-dev" - Tech Admin access
  is_read_only: boolean; // LDAP group "verity-read-only" - Base read-only access
  user_type: UserType;
  papi_info: UserDetailsFromCorpSystem;

  product_lines_access: ProductLineAccess[];
  item_metadata: ItemMetadata;
}

/**
 * Enhanced user permission with full product line information
 * Used in UI components that need complete product line details
 */
export interface EnhancedUserPermission extends Omit<UserPermission, 'product_lines_access'> {
  product_lines_access: EnhancedProductLineAccess[];
}

/**
 * Props for access management components
 */
export interface AccessManagementProps {
  userPermissions: UserPermission[];
  isLoading?: boolean;
  error?: string | null;
}

/**
 * Enhanced props for access management components that need full product line info
 */
export interface EnhancedAccessManagementProps {
  userPermissions: EnhancedUserPermission[];
  isLoading?: boolean;
  error?: string | null;
}

/**
 * Enhanced form data with validation metadata
 * Extends UserPermission with additional fields for form state management
 */
export interface UserPermissionFormWithValidation extends Omit<UserPermission, 'user_type' | 'user_access_id'> {
  user_access_id?: number | null; // Optional in form - will be set by backend
  is_valid_user_alias: boolean;
  user_type: UserType | undefined; // Allow undefined in form
}

/**
 * Enhanced form data with full product line information
 */
export interface EnhancedUserPermissionFormWithValidation extends Omit<
  EnhancedUserPermission,
  'user_type' | 'user_access_id'
> {
  user_access_id?: number | null; // Optional in form - will be set by backend
  is_valid_user_alias: boolean;
  user_type: UserType | undefined; // Allow undefined in form
}

/**
 * Form mode - determines if we're creating or editing access management
 */
export type AccessFormMode = 'create' | 'edit';
